import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProfessionalLayout from '../components/ProfessionalLayout';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { STATUS_LABELS, SHIPMENT_STATUS_LABELS, ROLE_LABELS } from '../lib/constants';
import {
  ArrowLeft, Package, Plane, Users, AlertTriangle, Mail, MessageSquare,
  Megaphone, Receipt, RefreshCw, Building2,
} from 'lucide-react';

const COMPANY_STATUS_BADGE = {
  active: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  suspended: 'bg-red-100 text-red-700',
};

const Kpi = ({ icon: Icon, label, value, sub, tone = 'default' }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium">
      <Icon size={15} className={tone === 'danger' ? 'text-red-500' : 'text-orange-500'} />
      {label}
    </div>
    <div className={`mt-2 text-2xl font-bold tabular-nums ${tone === 'danger' && value > 0 ? 'text-red-600' : ''}`}>
      {value}
    </div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

const Breakdown = ({ title, icon: Icon, data, labels }) => {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon size={16} className="text-orange-500" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune donnée</p>
        ) : (
          <ul className="space-y-2">
            {entries.map(([k, n]) => (
              <li key={k} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{(labels && labels[k]) || k}</span>
                <span className="font-medium tabular-nums">{n}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

const CompanyActivityPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchActivity = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/platform/companies/${companyId}/activity-detail`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Impossible de charger l'activité de l'entreprise.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  const fmtMoney = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));

  return (
    <ProfessionalLayout>
      <div className="p-4 sm:p-6 space-y-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate('/platform-admin')} className="gap-1">
            <ArrowLeft size={16} /> Entreprises
          </Button>
          <Button variant="outline" size="sm" onClick={fetchActivity} className="gap-1">
            <RefreshCw size={14} /> Actualiser
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
        ) : data && (
          <>
            {/* Header entreprise */}
            <Card>
              <CardContent className="p-5 flex items-start gap-4 flex-wrap">
                <div className="w-11 h-11 rounded-lg bg-orange-500/10 flex items-center justify-center">
                  <Building2 className="text-orange-500" size={22} />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-xl font-bold">{data.company.name}</h1>
                    <Badge className={COMPANY_STATUS_BADGE[data.company.status] || 'bg-gray-100 text-gray-700'}>
                      {data.company.status}
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                    {data.company.code && <span className="font-mono">{data.company.code}</span>}
                    {data.company.country && <span>{data.company.country}</span>}
                    {data.company.contact_email && <span>{data.company.contact_email}</span>}
                    {data.company.contact_phone && <span>{data.company.contact_phone}</span>}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* KPIs principaux */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi icon={Package} label="Colis (total)" value={data.packages.total}
                sub={`${data.packages.this_month} ce mois`} />
              <Kpi icon={Plane} label="Voyages (total)" value={data.shipments.total}
                sub={`${data.shipments.this_month} ce mois`} />
              <Kpi icon={Users} label="Utilisateurs" value={data.users.total} />
              <Kpi icon={AlertTriangle} label="Incidents ouverts" value={data.incidents.open}
                sub={`${data.incidents.total} au total`} tone="danger" />
            </div>

            {/* Messagerie ce mois */}
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm">Messagerie — mois en cours</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <Kpi icon={Mail} label="Emails" value={data.messaging_this_month.emails} />
                  <Kpi icon={MessageSquare} label="WhatsApp" value={data.messaging_this_month.whatsapp} />
                  <Kpi icon={Megaphone} label="Diffusions" value={data.messaging_this_month.broadcasts} />
                </div>
              </CardContent>
            </Card>

            {/* Facturation */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Kpi icon={Receipt} label="Factures" value={data.invoices.count} />
              <Kpi icon={Receipt} label="Facturé" value={fmtMoney(data.invoices.total_invoiced)} />
              <Kpi icon={Receipt} label="Payé" value={fmtMoney(data.invoices.total_paid)}
                sub={`Reste ${fmtMoney(data.invoices.total_invoiced - data.invoices.total_paid)}`} />
            </div>

            {/* Répartitions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Breakdown title="Colis par statut" icon={Package} data={data.packages.by_status} labels={STATUS_LABELS} />
              <Breakdown title="Voyages par statut" icon={Plane} data={data.shipments.by_status} labels={SHIPMENT_STATUS_LABELS} />
              <Breakdown title="Utilisateurs par rôle" icon={Users} data={data.users.by_role} labels={ROLE_LABELS} />
            </div>
          </>
        )}
      </div>
    </ProfessionalLayout>
  );
};

export default CompanyActivityPage;
